import React, { useCallback, useEffect, useState } from "react";
import Link from "next/link";
import useAssetRemoveBackground from "domains/assets/hooks/useAssetRemoveBackground";
import { useAssetRemoveBackgroundCu } from "domains/assets/hooks/useAssetRemoveBackgroundCu";
import { useScenarioAssetDrop } from "domains/assets/hooks/useScenarioAssetDrop";
import { loadImage } from "domains/canvas/components/sections/DrawingTools/mergeImage";
import { BoundingBoxImageClass } from "domains/canvas/enum/BoundingBoxImageClass";
import { classifyImageTransparency } from "domains/canvas/utils/classifyImageTransparency";
import formatDate from "domains/canvas/utils/formatDate";
import { useClipboardContext } from "domains/commons/contexts/ClipboardProvider";
import { CardPreview } from "domains/file-manager/components/FileManagerV2/FmCard";
import { getImageThumbnail } from "domains/file-manager/interfaces";
import { GuideKeys } from "domains/guide/constants/guides";
import ButtonDroppableUpload from "domains/image/components/ButtonDroppableUpload";
import { ModalLibraryAssetSelectProps } from "domains/image/components/ModalLibraryAssetSelect";
import SelectLibraryAsset from "domains/image/components/SelectLibraryAsset";
import { useAssetUpload } from "domains/image/hooks/useAssetUpload";
import useImageUploadDragDrop from "domains/image/hooks/useImageUploadDragDrop";
import { loadBase64FromUrl } from "domains/image/methods/loadDataFromUrl";
import ButtonSectionTopIcon from "domains/inference/components/InferenceGenerator/Sidebar/ButtonSectionTopIcon";
import SidebarSection from "domains/inference/components/InferenceGenerator/Sidebar/Section";
import { useSectionsContext } from "domains/inference/contexts/SectionsProvider";
import { ButtonWithModalMiniCanvas } from "domains/mini-canvas/components/MiniCanvas";
import { MiniCanvasProvider } from "domains/mini-canvas/contexts/MiniCanvasProvider";
import TopIconButton from "domains/models/components/ModelTrain/ElementTopIconButton";
import useRouter from "domains/navigation/hooks/useRouter";
import { useScenarioToast } from "domains/notification/hooks/useScenarioToast";
import { useTeamContext } from "domains/teams/contexts/TeamProvider";
import CompatibleImageUploader, {
  handleBlurFocusOnUploadClick,
  ImageListType,
} from "domains/ui/components/CompatibleImageUploader";
import Divider from "domains/ui/components/Divider";
import Icon from "domains/ui/components/Icon";
import MenuItem from "domains/ui/components/Menu/MenuItem";
import ModalCrop, { ModalCropProps } from "domains/ui/components/ModalCrop";
import { useHover } from "domains/ui/hooks/useHover";
import { useHandleApiError } from "infra/api/error";
import { removeCuFromType } from "infra/api/ExcludeCU";
import {
  GetAssetsByAssetIdApiResponse,
  usePostPatchInferencesMutation,
} from "infra/api/generated/api";
import mime from "mime-types";
import { useIntl } from "react-intl";

import {
  Box,
  BoxProps,
  Center,
  Flex,
  HStack,
  Image as ChakraImage,
  Menu,
  MenuButton,
  MenuList,
  Spinner,
  StackProps,
  Tag,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";

export interface SidebarSectionReferenceImageBaseProps {
  title: string;
  id: string;
  tooltip?: string;
  noCollapse?: boolean;
  asset: GetAssetsByAssetIdApiResponse["asset"] | undefined;
  setAsset: (asset: GetAssetsByAssetIdApiResponse["asset"] | undefined) => void;
  withSketch?: "always" | "edit" | "never";
  sketchDimensions?: [number, number];
  isLoading?: boolean;
  imageTag?: string;
  modeMapContent?: React.ReactNode;
  additionalContent?: React.ReactNode;
  removeBackgroundTrackingExtraParams: Record<string, unknown>;
  builderSectionHeaderStyle?: StackProps;
  builderSectionContentBoxStyle?: BoxProps;
  isPasteEnabled?: boolean;
  guide?: GuideKeys;
  withRemoveBackground?: boolean;
  isImmutable?: boolean;
  selectLibrary: {
    assetFilterTypeProps?: ModalLibraryAssetSelectProps["assetFilterTypeProps"];
    tabs: ModalLibraryAssetSelectProps["tabs"];
  };
  recommendedBackgroundTransparency?: "transparent" | "opaque";
  kind?: "image" | "video" | "3d";
}

export default function SidebarSectionReferenceImageBase({
  title,
  id,
  tooltip,
  noCollapse,
  asset,
  setAsset,
  withSketch = "always",
  sketchDimensions,
  isLoading = false,
  imageTag,
  modeMapContent,
  additionalContent,
  removeBackgroundTrackingExtraParams,
  builderSectionHeaderStyle,
  builderSectionContentBoxStyle,
  isPasteEnabled = true,
  guide,
  withRemoveBackground = true,
  isImmutable = false,
  selectLibrary,
  recommendedBackgroundTransparency,
  kind = "image",
}: SidebarSectionReferenceImageBaseProps) {
  const router = useRouter();
  const { formatMessage } = useIntl();
  const { selectedProject } = useTeamContext();
  const { successToast } = useScenarioToast();
  const { addImagePasteListener, removeImagePasteListener } =
    useClipboardContext();
  const [hoverRef, isHovered] = useHover<HTMLDivElement>();
  const { uploadImage, isUploading, uploadProgress } = useAssetUpload();

  const { toggleCollapsedSection } = useSectionsContext();
  const [imageToCrop, setImageToCrop] =
    useState<ModalCropProps["imageToCrop"]>();
  const [isImageUploadLoading, setIsImageUploadLoading] = useState(false);

  const { handleAssetUrl, isLoading: isAssetUrlLoading } = useScenarioAssetDrop(
    {
      onAssetFound: async (asset) => {
        await handleSetImage({
          asset,
        });
      },
      kind,
    }
  );

  const { isDraggingHover, dragFunctions, onDrop } = useImageUploadDragDrop({
    onImageDrop: handleAssetUrl,
  });

  // ----------------------------------
  const handleSetImage = useCallback(
    async (
      item:
        | {
            src: string;
            name: string;
          }
        | {
            asset: GetAssetsByAssetIdApiResponse["asset"];
          }
        | undefined
    ) => {
      if (!item || "asset" in item) {
        setAsset(item?.asset);
        return;
      }

      setIsImageUploadLoading(true);

      const uploadedAsset = await uploadImage({
        data: item.src,
        name: item.name,
        kind,
      });
      if (uploadedAsset) {
        setAsset(uploadedAsset);
        toggleCollapsedSection(id, false);
      }
      setIsImageUploadLoading(false);
    },
    [uploadImage, setAsset, toggleCollapsedSection, id, kind]
  );

  // ----------------------------------

  useEffect(() => {
    void (async () => {
      if (
        asset &&
        asset.ownerId !== selectedProject.id &&
        asset.privacy !== "public"
      ) {
        setAsset(undefined);
        setIsImageUploadLoading(true);
        const base64 = asset.url.startsWith("http")
          ? await loadBase64FromUrl(asset.url, kind)
          : asset.url;
        if (!base64) {
          return;
        }
        void handleSetImage({
          src: base64,
          name:
            asset.metadata.name ??
            `${asset.id}-copy.${mime.extension(asset.mimeType)}`,
        });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedProject.id]);

  // ----------------------------------

  const handleApiError = useHandleApiError();
  const [putImagePatchtrigger] = usePostPatchInferencesMutation();
  const handleSetImageForMiniCanvas = useCallback(
    async (newImage: string) => {
      if (!asset) {
        return handleSetImage({
          src: newImage,
          name: "reference-image-" + formatDate(Date.now()),
        });
      }

      setIsImageUploadLoading(true);

      const [imageA, imageB] = await Promise.all([
        loadImage(asset.url),
        loadImage(newImage),
      ]);
      const patch = document.createElement("canvas");
      patch.width = imageA.width;
      patch.height = imageA.height;
      const ctx = patch.getContext("2d");
      if (!ctx) {
        return;
      }
      ctx.globalCompositeOperation = "difference";
      ctx.drawImage(imageA, 0, 0);
      ctx.globalCompositeOperation = "source-over";
      ctx.drawImage(imageB, 0, 0);
      const patchData = patch.toDataURL("image/png");

      try {
        const patchImage = await putImagePatchtrigger({
          projectId: selectedProject.id,
          body: {
            image: asset.id,
            patch: {
              mode: "override",
              image: patchData,
            },
          },
        })
          .unwrap()
          .then(removeCuFromType);

        if (patchImage.asset) {
          await handleSetImage({
            asset: patchImage.asset,
          });
        }
      } catch (error) {
        handleApiError(error, "Error uploading image");
      } finally {
        setIsImageUploadLoading(false);
      }
    },
    [
      asset,
      handleApiError,
      handleSetImage,
      putImagePatchtrigger,
      selectedProject.id,
    ]
  );

  // ----------------------------------

  const [imageTransparency, setImageTransparency] = useState<
    BoundingBoxImageClass | undefined
  >(undefined);
  useEffect(() => {
    if (!asset || !recommendedBackgroundTransparency) {
      setImageTransparency(undefined);
      return;
    }
    void classifyImageTransparency(asset.url).then((transparency) => {
      setImageTransparency(transparency);
    });
  }, [asset, recommendedBackgroundTransparency]);

  const { handleRemoveBackgroundAsset, isRemoveBackgroundLoading } =
    useAssetRemoveBackground();
  const onRemoveBackground = useCallback(async () => {
    if (!asset) {
      return;
    }
    const newAsset = await handleRemoveBackgroundAsset({
      assetId: asset.id,
      trackingExtraParams: removeBackgroundTrackingExtraParams,
    });
    if (newAsset) {
      void handleSetImage({
        asset: newAsset,
      });
    }
  }, [
    asset,
    handleRemoveBackgroundAsset,
    handleSetImage,
    removeBackgroundTrackingExtraParams,
  ]);

  const handleUploadInputChange = useCallback(
    async (imageList: ImageListType) => {
      if (imageList.length === 0) {
        return;
      }

      const dataUrl = imageList[0].dataURL;
      if (dataUrl === undefined) {
        return;
      }

      await handleSetImage({
        src: dataUrl,
        name: imageList[0].name,
      });
    },
    [handleSetImage]
  );

  // ----------------------------------

  useEffect(() => {
    if (!isPasteEnabled) {
      return;
    }
    const onImagePastedCallback = async (images: string[]) => {
      if (images.length > 0) {
        await handleSetImage({
          src: images[0],
          name: "reference-image-" + formatDate(Date.now()),
        });
        successToast({
          title: "Image imported from clipboard",
        });
      }
    };

    addImagePasteListener(onImagePastedCallback);

    return () => {
      removeImagePasteListener(onImagePastedCallback);
    };
  }, [
    addImagePasteListener,
    removeImagePasteListener,
    handleSetImage,
    successToast,
    isPasteEnabled,
  ]);

  const canRemoveBackground =
    withRemoveBackground &&
    asset?.kind === "image" &&
    recommendedBackgroundTransparency !== "opaque";
  const { cuCost, isCuLoading } = useAssetRemoveBackgroundCu({
    assetId: canRemoveBackground ? asset?.id : undefined,
  });

  const canCrop = asset?.kind === "image";

  const handleCropCancel = useCallback(() => {
    setImageToCrop(undefined);
  }, [setImageToCrop]);

  const handleCropSubmit = useCallback(
    async (asset: GetAssetsByAssetIdApiResponse["asset"]) => {
      await handleSetImage({
        asset,
      });
      setImageToCrop(undefined);
    },
    [handleSetImage, setImageToCrop]
  );

  return (
    <MiniCanvasProvider>
      <SidebarSection
        key={isLoading ? `${id}Loading` : id}
        headerProps={{
          id,
          title,
          tooltip,
          noCollapse: isImmutable || noCollapse,
          topRight:
            asset && !isImmutable ? (
              <HStack spacing={2}>
                <ButtonSectionTopIcon onClick={() => handleSetImage(undefined)}>
                  <Icon id="Ui/TrashMd" h="14px" />
                </ButtonSectionTopIcon>

                {withSketch !== "never" && (
                  <ButtonWithModalMiniCanvas
                    defaultImage={asset.url}
                    onAccept={handleSetImageForMiniCanvas}
                    canvasDimensions={sketchDimensions}
                    CustomButton={ButtonSectionTopIcon}
                  >
                    <Icon id="Ui/Pencil" h="14px" />
                  </ButtonWithModalMiniCanvas>
                )}

                {(canCrop || canRemoveBackground) && (
                  <Menu placement="bottom-end">
                    <MenuButton
                      as={ButtonSectionTopIcon}
                      isLoading={isRemoveBackgroundLoading}
                    >
                      <Flex justify="center" w="100%">
                        <Icon id="Ui/Dots" />
                      </Flex>
                    </MenuButton>

                    <MenuList>
                      {canCrop && (
                        <MenuItem
                          onClick={() =>
                            setImageToCrop({
                              assetId: asset.id,
                              src: asset.url,
                            })
                          }
                          text={formatMessage({
                            id: "domains.inference.inferenceGenerator.cropImage",
                            defaultMessage: "Crop Image",
                          })}
                        />
                      )}

                      {canRemoveBackground && (
                        <MenuItem
                          onClick={onRemoveBackground}
                          text={formatMessage({
                            id: "domains.inference.inferenceGenerator.removeBackground",
                            defaultMessage: "Remove Background",
                          })}
                          cuCost={{
                            cost: cuCost,
                            isLoading: isCuLoading,
                          }}
                        />
                      )}
                    </MenuList>
                  </Menu>
                )}
              </HStack>
            ) : undefined,
          style: builderSectionHeaderStyle,
          guide,
        }}
        contentBoxStyle={builderSectionContentBoxStyle}
      >
        <Flex ref={hoverRef} w="full" data-testid={`${id}-section`}>
          <CompatibleImageUploader
            value={[]}
            onChange={handleUploadInputChange}
            allowNonImageType={kind !== "image"}
            acceptType={kind === "video" ? ["mp4"] : undefined}
            maxNumber={1}
            multiple={false}
          >
            {({ onImageUpload, dragProps: { onDrop: dragPropsOnDrop } }) => {
              if (asset) {
                return (
                  <Flex align="center" direction="column" gap={4} w="100%">
                    <HStack spacing={2}>
                      {isImageUploadLoading ||
                      isLoading ||
                      isAssetUrlLoading ||
                      isUploading ? (
                        <Center
                          w="160px"
                          h="160px"
                          borderRadius="md"
                          bgColor="backgroundQuaternary.500"
                        >
                          <Spinner />
                        </Center>
                      ) : (
                        <Box
                          {...dragFunctions}
                          pos="relative"
                          onDrop={(e) => onDrop(e, dragPropsOnDrop)}
                        >
                          <Link
                            href={{
                              query: {
                                ...router.query,
                                openAssetId: asset.id,
                              },
                            }}
                          >
                            <ChakraImage
                              maxH="160px"
                              borderRadius="md"
                              alt="User uploaded image"
                              data-testid={`${id}-drag-and-drop-alt-image-area`}
                              fallback={
                                <Center
                                  w="160px"
                                  h="160px"
                                  borderRadius="md"
                                  bgColor="backgroundQuaternary.500"
                                >
                                  <Spinner />
                                </Center>
                              }
                              src={getImageThumbnail({
                                asset,
                              })}
                            />
                            {asset.preview && (
                              <CardPreview
                                preview={asset.preview.url}
                                isHovered={isHovered}
                                objectFit="contain"
                              />
                            )}
                            {imageTag && (
                              <Box pos="absolute" bottom={2} left={2}>
                                <Tag variant="secondary">{imageTag}</Tag>
                              </Box>
                            )}
                            {!!recommendedBackgroundTransparency &&
                              !!imageTransparency &&
                              (recommendedBackgroundTransparency ===
                                "transparent") ===
                                (imageTransparency ===
                                  BoundingBoxImageClass.ZeroTransparent) && (
                                <BackgroundWarning
                                  size="md"
                                  recommendedBackgroundTransparency={
                                    recommendedBackgroundTransparency
                                  }
                                />
                              )}
                          </Link>
                        </Box>
                      )}

                      {modeMapContent}
                    </HStack>

                    {additionalContent}
                  </Flex>
                );
              } else if (!isImmutable) {
                return (
                  <VStack w="100%" spacing={2}>
                    <SelectLibraryAsset
                      onSelect={(asset) => {
                        void handleSetImage({
                          asset: asset.meta,
                        });
                      }}
                      assetFilterTypeProps={selectLibrary?.assetFilterTypeProps}
                      tabs={selectLibrary?.tabs}
                    />

                    {withSketch === "always" && (
                      <ButtonWithModalMiniCanvas
                        onAccept={(image) =>
                          handleSetImage({
                            src: image,
                            name: "reference-image-" + formatDate(Date.now()),
                          })
                        }
                        canvasDimensions={sketchDimensions}
                        variant="secondary"
                        size="sm"
                        w="100%"
                      >
                        Sketch
                      </ButtonWithModalMiniCanvas>
                    )}

                    <Divider label="OR" w="100%" />

                    <ButtonDroppableUpload
                      isLoading={
                        isImageUploadLoading ||
                        isLoading ||
                        isAssetUrlLoading ||
                        isUploading
                      }
                      loadingProgress={uploadProgress}
                      isDraggingHover={isDraggingHover}
                      onClick={() =>
                        handleBlurFocusOnUploadClick(
                          onImageUpload,
                          setIsImageUploadLoading
                        )
                      }
                      onDrop={(e) => onDrop(e, dragPropsOnDrop)}
                      type={kind}
                      {...dragFunctions}
                    />
                  </VStack>
                );
              } else {
                return (
                  <Text color="textSecondary" size="body.sm">
                    -
                  </Text>
                );
              }
            }}
          </CompatibleImageUploader>
        </Flex>
      </SidebarSection>

      {!!asset && (
        <ModalCrop
          imageToCrop={imageToCrop}
          onSubmit={handleCropSubmit}
          onCancel={handleCropCancel}
        />
      )}
    </MiniCanvasProvider>
  );
}

export function BackgroundWarning({
  size,
  recommendedBackgroundTransparency,
}: {
  size: "sm" | "md";
  recommendedBackgroundTransparency: "transparent" | "opaque";
}) {
  return (
    <Tooltip
      label={
        recommendedBackgroundTransparency === "transparent"
          ? `Remove the background to improve output quality.`
          : `Transparent backgrounds may reduce video accuracy. For best results, use images with full backgrounds.`
      }
    >
      <Box pos="absolute" top={2} left={2}>
        <TopIconButton
          pointerEvents="none"
          w={size === "sm" ? "20px" : "28px"}
          h={size === "sm" ? "20px" : "28px"}
          minW={size === "sm" ? "20px" : "28px"}
        >
          <Icon
            id="Ui/AlertWarning"
            color="warning.500"
            h={size === "sm" ? "10px" : "14px"}
          />
        </TopIconButton>
      </Box>
    </Tooltip>
  );
}
